"use client";

import { motion } from "framer-motion";
import { Check, CheckCheck, ImageIcon } from "lucide-react";
import type { ChatMessage } from "@/lib/chat-store";
import { cn } from "@/lib/utils";

interface ChatBubbleProps {
  message: ChatMessage;
  isLast: boolean;
}

function formatTime(ts: number) {
  const d = new Date(ts);
  const hours = d.getHours().toString().padStart(2, "0");
  const minutes = d.getMinutes().toString().padStart(2, "0");
  return `${hours}.${minutes}`;
}

function StatusIndicator({ status }: { status: ChatMessage["status"] }) {
  if (status === "sending") {
    return (
      <Check className="w-3.5 h-3.5 text-dark-text-secondary/50 dark:text-dark-text-secondary/50 text-light-text-secondary/50" />
    );
  }

  if (status === "sent") {
    return (
      <Check className="w-3.5 h-3.5 text-dark-text-secondary/50 dark:text-dark-text-secondary/50 text-light-text-secondary/50" />
    );
  }

  if (status === "delivered") {
    return (
      <CheckCheck className="w-3.5 h-3.5 text-dark-text-secondary/50 dark:text-dark-text-secondary/50 text-light-text-secondary/50" />
    );
  }

  return (
    <CheckCheck className="w-3.5 h-3.5 text-gold" />
  );
}

export function ChatBubble({ message, isLast }: ChatBubbleProps) {
  const isCustomer = message.senderRole === "customer";

  return (
    <motion.div
      initial={{ opacity: 0, x: isCustomer ? 20 : -20, y: 8 }}
      animate={{ opacity: 1, x: 0, y: 0 }}
      transition={{
        type: "spring",
        stiffness: 400,
        damping: 30,
      }}
      className={cn("flex flex-col", isCustomer ? "items-end" : "items-start")}
    >
      {/* Sender name for admin messages */}
      {!isCustomer && (
        <span
          className={cn(
            "text-[11px] font-medium mb-1 ml-3",
            "text-gold dark:text-gold"
          )}
        >
          {message.senderName}
        </span>
      )}

      {/* Attachment thumbnail */}
      {message.attachment && message.attachment.type === "image" && (
        <div
          className={cn(
            "mb-1 overflow-hidden rounded-[16px] max-w-[220px]",
            "border border-white/[0.08] dark:border-white/[0.08] border-black/[0.06]"
          )}
        >
          <div className="w-full aspect-[4/3] bg-dark-card dark:bg-dark-card bg-light-card flex items-center justify-center">
            <ImageIcon className="w-8 h-8 text-dark-text-secondary/30 dark:text-dark-text-secondary/30 text-light-text-secondary/30" />
          </div>
          <div
            className={cn(
              "px-3 py-1.5",
              "bg-dark-card/60 dark:bg-dark-card/60 bg-light-card/60",
              "text-[11px] text-dark-text-secondary dark:text-dark-text-secondary text-light-text-secondary"
            )}
          >
            {message.attachment.name}
          </div>
        </div>
      )}

      {/* Bubble */}
      <div
        className={cn(
          "relative max-w-[75%] px-4 py-2.5",
          isCustomer
            ? [
                "bg-gold/90 text-white",
                "rounded-[20px] rounded-br-[6px]",
                "shadow-md shadow-gold/15",
              ]
            : [
                "bg-dark-card dark:bg-dark-card bg-light-card",
                "text-dark-text-primary dark:text-dark-text-primary text-light-text-primary",
                "rounded-[20px] rounded-bl-[6px]",
                "border border-white/[0.06] dark:border-white/[0.06] border-black/[0.06]",
              ]
        )}
      >
        <p className="text-sm leading-[1.5] whitespace-pre-wrap break-words">
          {message.text}
        </p>
      </div>

      {/* Timestamp + Status */}
      <div
        className={cn(
          "flex items-center gap-1 mt-0.5",
          isCustomer ? "flex-row-reverse mr-0.5" : "ml-0.5"
        )}
      >
        <span
          className={cn(
            "text-[10px] leading-none",
            "text-dark-text-secondary/60 dark:text-dark-text-secondary/60 text-light-text-secondary/60"
          )}
        >
          {formatTime(message.timestamp)}
        </span>
        {isCustomer && <StatusIndicator status={message.status} />}
      </div>
    </motion.div>
  );
}
